import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import './EpubPage.css';
import './UploadCard.css';
import Button from './Button';
import AddToListsModal from './AddToListsModal';
import {
  cacheOfflineBook,
  getOfflineBookMetadata,
  isOfflineBookAvailable,
  removeOfflineBook
} from '../utils/offlineLibrary';
import {
  buildDerivedEpubWithAppendedChapters,
  buildOfflineEpubBlob,
  fetchAppendedChapters,
  fetchOfflineReadyBookMetadata
} from "../utils/appendedChapterEpub";
import {
  cacheBookListStatus,
  flushPendingBookListUpdates,
  getCachedBookListStatus
} from '../utils/bookListCache';
const IMG_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') ?? '';

function getUploaderPictureUrl(uploaderPicture) {
  if (!uploaderPicture) return null;
  if (/^https?:\/\//i.test(uploaderPicture)) return uploaderPicture;
  return `${IMG_BASE}/${String(uploaderPicture).replace(/^\/+/, '')}`;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr.replace(' ', 'T') + 'Z').getTime();
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  if (weeks < 5) return `${weeks}w ago`;
  if (months < 12) return `${months}mo ago`;
  return `${years}yr ago`;
}

function SeriesWorkCard({ id, title, seriesTitle, seriesOrder, uploadedBy, uploaderPicture, createdAt }) {
  const navigate = useNavigate();
  const [avatarFailed, setAvatarFailed] = useState(false);
  const avatarUrl = avatarFailed ? null : getUploaderPictureUrl(uploaderPicture);
  const uploaderInitial = String(uploadedBy || 'R').trim().charAt(0).toUpperCase() || 'R';
  const seriesLabel = seriesOrder
    ? `#${seriesOrder} of ${seriesTitle}`
    : seriesTitle;

  return (
    <div
      className="upload-card-wrapper p-3 d-flex align-items-center gap-4 rounded-2"
      onClick={() => navigate(`/epub/${id}`)}
      role="button"
      tabIndex={0}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          navigate(`/epub/${id}`);
        }
      }}
    >
      <div className="uploads-left d-flex align-items-center justify-content-center">
        <span className="uploads-left-letter">
          {title?.charAt(0).toUpperCase()}
        </span>
      </div>

      <div className="uploads-middle d-flex flex-column gap-1">
        <h5 className="mb-0">{title}</h5>
        <p className="uploads-author mb-2">{seriesLabel}</p>

        <div className="d-flex align-items-center gap-2 pt-2">
          <div className="uploader-avatar">
            {avatarUrl ? (
              <img src={avatarUrl} alt="" onError={() => setAvatarFailed(true)} />
            ) : (
              <span className="uploader-avatar__fallback">{uploaderInitial}</span>
            )}
          </div>
          <p className="uploads-uploader-text mb-0">
            Uploaded by <strong>{uploadedBy}</strong>
          </p>
        </div>
      </div>

      <div className="uploads-right ms-auto">
        {createdAt && (
          <div className="d-inline-block p-1">
            <span className="uploads-timestamp">{timeAgo(createdAt)}</span>
          </div>
        )}
      </div>
    </div>
  );
}
function EpubPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [epub, setEpub] = useState(null);
  const [titleWraps, setTitleWraps] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [showAddToLists, setShowAddToLists] = useState(false);
  const [listStatus, setListStatus] = useState({
    bookmarks: false,
    finished: false,
    tbr: false
  });
  const [editForm, setEditForm] = useState({});
  const [commentCount, setCommentCount] = useState(0);
  const [wordCount, setWordCount] = useState(null);
  const [bookmarks, setBookmarks] = useState([]);
  const [bookmarksLoading, setBookmarksLoading] = useState(true);
  const [seriesWorks, setSeriesWorks] = useState([]);
  const [seriesWorksLoading, setSeriesWorksLoading] = useState(false);
  const [isOfflineAvailable, setIsOfflineAvailable] = useState(false);
  const [isOfflineActionRunning, setIsOfflineActionRunning] = useState(false);
  const titleRef = useRef(null);
  const apiUrl = process.env.REACT_APP_API_URL;
  const currentUserId = JSON.parse(localStorage.getItem('user') || '{}').id || 0;

  useEffect(() => {
    document.body.style.backgroundColor = '#f9f6f6';
    return () => { document.body.style.backgroundColor = ''; };
  }, []);

  const mergeProgressSnapshot = (previousBook, nextBook) => {
    if (!previousBook) return nextBook;
    if (!nextBook) return previousBook;
    if (Number(previousBook.id) !== Number(nextBook.id)) return nextBook;

    const previousProgress = Number(previousBook.progress_percent || 0);
    const nextProgress = Number(nextBook.progress_percent || 0);
    const previousHasLocation =
      Boolean(previousBook.reading_cfi) || Boolean(previousBook.section_href);
    const nextHasLocation =
      Boolean(nextBook.reading_cfi) || Boolean(nextBook.section_href);

    const shouldKeepPreviousProgress =
      previousHasLocation &&
      (
        !nextHasLocation ||
        nextProgress < previousProgress ||
        (nextProgress <= 0 && previousProgress > 0)
      );

    if (!shouldKeepPreviousProgress) {
      return nextBook;
    }

    return {
      ...nextBook,
      progress_percent: previousBook.progress_percent,
      reading_cfi: previousBook.reading_cfi,
      chapter_title: previousBook.chapter_title,
      section_href: previousBook.section_href,
      section_scroll_ratio: previousBook.section_scroll_ratio
    };
  };

  const fetchEpub = () => {
    if (!navigator.onLine) {
      const offlineMetadata = getOfflineBookMetadata(currentUserId, Number(id));
      if (offlineMetadata) {
        setEpub((prev) => mergeProgressSnapshot(prev, offlineMetadata));
      }
      return;
    }

    fetch(`${apiUrl}/get-epub.php?id=${id}&uid=${currentUserId}`, { credentials: 'include' })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setEpub((prev) => mergeProgressSnapshot(prev, data.epub));
        }
      })
      .catch(console.error);
  };

  const fetchListStatus = () => {
    if (!apiUrl || !id || !currentUserId) return;

    const cachedStatus = getCachedBookListStatus(currentUserId, id);
    if (cachedStatus) {
      setListStatus({
        bookmarks: Boolean(cachedStatus.bookmarks),
        finished: Boolean(cachedStatus.finished),
        tbr: Boolean(cachedStatus.tbr)
      });
    }

    if (!navigator.onLine) return;

    flushPendingBookListUpdates(apiUrl).catch(() => {});

    fetch(`${apiUrl}/get-book-list-status.php?uid=${currentUserId}&epub_id=${id}`, {
      credentials: 'include',
      cache: 'no-store'
    })
      .then((r) => r.json())
      .then((data) => {
        if (!data?.success) return;
        setListStatus({
          bookmarks: Boolean(data.status?.bookmarks),
          finished: Boolean(data.status?.finished),
          tbr: Boolean(data.status?.tbr)
        });
        cacheBookListStatus(currentUserId, id, {
          bookmarks: Boolean(data.status?.bookmarks),
          finished: Boolean(data.status?.finished),
          tbr: Boolean(data.status?.tbr),
          bookmark_note: data.status?.bookmark_note || ""
        });
      })
      .catch(console.error);
  };

  const fetchBookmarks = () => {
    if (!apiUrl || !id) {
      setBookmarks([]);
      setBookmarksLoading(false);
      return;
    }

    setBookmarksLoading(true);
    fetch(`${apiUrl}/get-epub-bookmarks.php?epub_id=${id}`, {
      credentials: 'include',
      cache: 'no-store'
    })
      .then((r) => r.json())
      .then((data) => {
        if (data?.success) {
          setBookmarks(Array.isArray(data.items) ? data.items : []);
        }
      })
      .catch(console.error)
      .finally(() => {
        setBookmarksLoading(false);
      });
  };

  useEffect(() => {
    fetchEpub();
  }, [id]);

  useEffect(() => {
    fetchListStatus();
  }, [apiUrl, currentUserId, id]);

  useEffect(() => {
    fetchBookmarks();
  }, [apiUrl, id]);
  useEffect(() => {
    const seriesTitle = (epub?.series_title || '').trim();
    if (!apiUrl || !seriesTitle || !id) {
      setSeriesWorks([]);
      setSeriesWorksLoading(false);
      return;
    }

    let cancelled = false;
    setSeriesWorksLoading(true);

    const loadSeriesWorks = async () => {
      try {
        const collected = [];
        let offset = 0;
        let hasMore = true;

        while (hasMore && !cancelled) {
          const response = await fetch(`${apiUrl}/get-epubs.php?limit=15&offset=${offset}`, {
            credentials: 'include',
            cache: 'no-store'
          });
          const data = await response.json();
          if (!data?.success) break;

          const pageItems = Array.isArray(data.epubs) ? data.epubs : [];
          collected.push(...pageItems);
          hasMore = Boolean(data.has_more);
          offset += pageItems.length;

          if (!pageItems.length) break;
        }

        if (cancelled) return;

        const normalizedSeriesTitle = seriesTitle.toLowerCase();
        const works = collected
          .filter((item) => Number(item.id) !== Number(id))
          .filter((item) => String(item.series_title || '').trim().toLowerCase() === normalizedSeriesTitle)
          .sort((a, b) => {
            const aOrder = Number(a.series_order);
            const bOrder = Number(b.series_order);
            const aHasOrder = Number.isFinite(aOrder) && aOrder > 0;
            const bHasOrder = Number.isFinite(bOrder) && bOrder > 0;

            if (aHasOrder !== bHasOrder) return aHasOrder ? -1 : 1;
            if (aHasOrder && bHasOrder && aOrder !== bOrder) return aOrder - bOrder;
            return Number(a.id || 0) - Number(b.id || 0);
          });

        setSeriesWorks(works);
      } catch (error) {
        console.error(error);
        if (!cancelled) setSeriesWorks([]);
      } finally {
        if (!cancelled) setSeriesWorksLoading(false);
      }
    };

    loadSeriesWorks();

    return () => {
      cancelled = true;
    };
  }, [apiUrl, epub?.series_title, id]);

  useEffect(() => {
    window.addEventListener('reader-closed', fetchEpub);
    return () => window.removeEventListener('reader-closed', fetchEpub);
  }, [id]);

  useEffect(() => {
    if (!apiUrl || !id || !currentUserId) return undefined;

    let cancelled = false;

    const loadCommentCount = async () => {
      try {
        const response = await fetch(
          `${apiUrl}/get-comment-cfis.php?epub_id=${id}&uid=${currentUserId}`,
          { credentials: 'include', cache: 'no-store' }
        );
        const data = await response.json();
        if (!data?.success || cancelled) return;

        const cfis = Array.isArray(data.cfis) ? data.cfis : [];
        const inlineCount = cfis.every((item) => item?.comment_count != null);

        if (inlineCount) {
          const total = cfis.reduce(
            (sum, item) => sum + Number(item.comment_count || 0),
            0
          );
          if (!cancelled) setCommentCount(total);
          return;
        }

        const totals = await Promise.all(
          cfis.map(async (item) => {
            try {
              const commentResponse = await fetch(
                `${apiUrl}/get-comments.php?epub_id=${id}&cfi=${encodeURIComponent(item.cfi)}&uid=${currentUserId}`,
                { credentials: 'include', cache: 'no-store' }
              );
              const commentData = await commentResponse.json();
              return commentData?.success && Array.isArray(commentData.comments)
                ? commentData.comments.length
                : 0;
            } catch (error) {
              return 0;
            }
          })
        );

        if (!cancelled) {
          setCommentCount(totals.reduce((sum, count) => sum + count, 0));
        }
      } catch (error) {
        console.error('Failed to load comment count:', error);
      }
    };

    loadCommentCount();

    return () => {
      cancelled = true;
    };
  }, [apiUrl, currentUserId, id]);

  useEffect(() => {
    if (!apiUrl || !id) return undefined;

    let cancelled = false;

    const loadWordCount = async () => {
      try {
        const rememberToken = localStorage.getItem("rememberToken") || "";
        const appendedChapters = navigator.onLine
          ? await fetchAppendedChapters({
              apiUrl,
              epubId: Number(id),
              rememberToken,
            })
          : [];
        const appendedFingerprint = appendedChapters
          .map((chapter) =>
            [
              chapter?.id ?? "",
              chapter?.chapter_order ?? "",
              chapter?.updated_at ?? chapter?.created_at ?? "",
            ].join(":")
          )
          .join("|");
        const cacheKey = `epub-word-count-${id}-${appendedFingerprint || "base"}`;
        const cached = localStorage.getItem(cacheKey);
        if (cached) {
          const parsed = Number(cached);
          if (!Number.isNaN(parsed) && parsed > 0) {
            if (!cancelled) {
              setWordCount(parsed);
            }
            return;
          }
        }

        const response = await fetch(`${apiUrl}/serve-epub.php?id=${id}&include_appended=0`, {
          credentials: 'include',
          cache: 'no-store',
          headers: rememberToken ? { "X-Remember-Token": rememberToken } : {}
        });
        let buffer = await response.arrayBuffer();
        if (appendedChapters.length) {
          buffer = await buildDerivedEpubWithAppendedChapters(buffer, appendedChapters);
        }
        const module = await import('epubjs');
        const ePub = module.default;
        const book = ePub(buffer);
        await book.ready;

        const sections = [];
        book.spine.each((section) => {
          if (section?.linear !== false) sections.push(section);
        });

        let totalWords = 0;
        for (const section of sections) {
          try {
            const contents = await section.load(book.load.bind(book));
            const text =
              section.document?.body?.textContent ||
              contents?.textContent ||
              '';
            const cleaned = text.replace(/\s+/g, ' ').trim();
            if (cleaned) {
              totalWords += cleaned.split(' ').filter(Boolean).length;
            }
            section.unload?.();
          } catch (error) {}
        }

        book.destroy?.();

        if (!cancelled) {
          setWordCount(totalWords > 0 ? totalWords : 0);
          localStorage.setItem(cacheKey, String(totalWords > 0 ? totalWords : 0));
        }
      } catch (error) {
        console.error('Failed to calculate word count:', error);
      }
    };

    loadWordCount();

    return () => {
      cancelled = true;
    };
  }, [apiUrl, id]);

  useEffect(() => {
    const syncOfflineState = () => {
      setIsOfflineAvailable(isOfflineBookAvailable(currentUserId, Number(id)));
    };

    syncOfflineState();
    window.addEventListener('offline-library-updated', syncOfflineState);
    return () => window.removeEventListener('offline-library-updated', syncOfflineState);
  }, [currentUserId, id]);

  useEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    const check = () => {
      const lh = parseFloat(getComputedStyle(el).lineHeight) || 28;
      setTitleWraps(el.clientHeight > lh * 1.5);
    };
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, [epub]);

  const handleEditOpen = () => {
    setEditForm({
      title: epub.title || '',
      author: epub.author || '',
      summary: epub.summary || '',
      content_type: epub.content_type || '',
      fandom_details: epub.fandom_details || '',
      series_title: epub.series_title || '',
      series_order: epub.series_order || '',
    });
    setShowEdit(true);
  };

  const handleSaveEdit = () => {
    const form = new FormData();
    form.append('id', id);
    Object.entries(editForm).forEach(([k, v]) => form.append(k, v));
    fetch(`${process.env.REACT_APP_API_URL}/update-epub.php`, {
      method: 'POST', credentials: 'include', body: form,
    })
      .then(r => r.json())
      .then(data => {
        if (data.success) {
          setEpub(prev => ({ ...prev, ...editForm }));
          setShowEdit(false);
        }
      })
      .catch(console.error);
  };

  const handleOfflineToggle = async () => {
    if (!epub || !apiUrl || !currentUserId || isOfflineActionRunning) return;

    if (isOfflineAvailable) {
      const shouldRemove = window.confirm('Remove book from your offline reads?');
      if (!shouldRemove) return;

      setIsOfflineActionRunning(true);
      try {
        await removeOfflineBook(currentUserId, Number(id));
        setIsOfflineAvailable(false);
      } catch (error) {
        console.error('Failed to remove offline book:', error);
      } finally {
        setIsOfflineActionRunning(false);
      }
      return;
    }

    setIsOfflineActionRunning(true);
    try {
      const rememberToken = localStorage.getItem("rememberToken") || "";
      const offlineMetadata = await fetchOfflineReadyBookMetadata({
        apiUrl,
        epubId: Number(id),
        userId: currentUserId,
        rememberToken,
        fallback: epub
      });
      const offlinePayload = await buildOfflineEpubBlob({
        apiUrl,
        epubId: Number(id),
        rememberToken
      });

      await cacheOfflineBook({
        userId: currentUserId,
        epubId: Number(id),
        metadata: offlineMetadata || epub,
        blob: offlinePayload
      });
      setIsOfflineAvailable(true);
    } catch (error) {
      console.error('Failed to cache offline book:', error);
    } finally {
      setIsOfflineActionRunning(false);
    }
  };

  const progressPercent = Number(epub?.progress_percent || 0);
  const isInAnyList = listStatus.bookmarks || listStatus.finished || listStatus.tbr;
  const hasStartedReading = Boolean(epub?.reading_cfi) || progressPercent > 0;
  const progressLabel =
    progressPercent <= 0
      ? "1%"
      : `${Math.round(progressPercent)}%`;
  const progressText =
    hasStartedReading && epub?.chapter_title
      ? `${epub.chapter_title} • ${progressLabel} complete`
      : hasStartedReading
        ? `${progressLabel} complete`
        : "Not started • 0% complete";
  const progressWidth = hasStartedReading
    ? `${Math.min(100, Math.max(progressPercent, 1))}%`
    : "0%";

  const formatBookmarkDate = (dateString) =>
    dateString
      ? new Date(dateString.replace(' ', 'T') + 'Z').toLocaleDateString('en-GB')
      : '';

  const getBookmarkInitial = (name) =>
    (name || 'R').trim().charAt(0).toUpperCase();

  if (!epub) return null;

  return (
    <div className="epub-page">
      {showAddToLists && epub && (
        <AddToListsModal
          book={epub}
          onClose={() => setShowAddToLists(false)}
          onSaved={(data) => {
            if (data?.status) {
              setListStatus({
                bookmarks: Boolean(data.status.bookmarks),
                finished: Boolean(data.status.finished),
                tbr: Boolean(data.status.tbr)
              });
            } else {
              fetchListStatus();
            }
            fetchEpub();
            fetchBookmarks();
          }}
        />
      )}

      {/* Hero: circle background with top bar + cover */}
      <div className={`epub-hero mb-4${epub.series_title ? ' epub-hero--series' : ''}${titleWraps ? ' epub-hero--long-title' : ''}`}>
        <div className="epub-hero-inner">

          <div className="d-flex justify-content-between align-items-start pt-4">
            <span className="material-symbols-rounded epub-icon-btn epub-icon-btn--tap" onClick={() => navigate(-1)}>arrow_back</span>
            <div className="text-center" style={{ minWidth: 0, maxWidth: '65%' }}>
              <div className="epub-page-title" ref={titleRef}>{epub.title}</div>
              <div className={`epub-page-author ${epub.series_title ? 'mt-2' : 'mt-1'}`}>{epub.author}</div>
              {epub.series_title && (
                <div className="epub-page-author ">
                  {epub.series_order ? `#${epub.series_order} of ` : ''}{epub.series_title}
                </div>
              )}
            </div>
            <span
              className="material-symbols-rounded epub-icon-btn epub-menu-btn"
              onClick={handleEditOpen}
            >more_horiz</span>
          </div>

        </div>

        <div className="epub-cover-wrapper">
          <div className="epub-cover-art d-flex align-items-center justify-content-center">
            <span className="epub-cover-letter">
              {epub.title?.charAt(0).toUpperCase()}
            </span>
          </div>
        </div>

      </div>

      {/* Edit details sheet */}
      {showEdit && (
        <div className="epub-edit-overlay" onClick={(e) => { if (e.target === e.currentTarget) setShowEdit(false); }}>
          <div className="epub-edit-sheet">
            <div className="epub-edit-header">
              <span className="epub-edit-heading">Edit Details</span>
              <span className="material-symbols-rounded epub-edit-close" onClick={() => setShowEdit(false)}>close</span>
            </div>
            <div className="epub-edit-body">
              {[
                { key: 'title', placeholder: 'Title' },
                { key: 'author', placeholder: 'Author' },
                { key: 'series_title', placeholder: 'Series' },
                { key: 'series_order', placeholder: 'Series #', type: 'number' },
              ].map(({ key, placeholder, type }) => (
                <div key={key} className="epub-edit-field">
                  <input
                    className="btn-neutral"
                    type={type || 'text'}
                    placeholder={placeholder}
                    value={editForm[key] || ''}
                    onChange={e => setEditForm(f => ({ ...f, [key]: e.target.value }))}
                  />
                </div>
              ))}

              <div className="epub-edit-field">
                <textarea
                  className="btn-neutral epub-edit-textarea"
                  placeholder="Synopsis"
                  value={editForm.summary || ''}
                  ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                  onChange={e => {
                    e.target.style.height = 'auto';
                    e.target.style.height = e.target.scrollHeight + 'px';
                    setEditForm(f => ({ ...f, summary: e.target.value }));
                  }}
                />
              </div>

              <div className="epub-edit-field epub-edit-select-wrapper">
                <select
                  className="btn-neutral epub-edit-select"
                  value={editForm.content_type || ''}
                  onChange={e => setEditForm(f => ({ ...f, content_type: e.target.value, fandom_details: '' }))}
                  style={{ color: editForm.content_type ? '#430F10' : 'rgba(67,15,16,0.4)' }}
                >
                  <option value="" disabled>Type</option>
                  <option value="Fiction">Fic</option>
                  <option value="Book">Book</option>
                </select>
                <span className="material-symbols-rounded epub-edit-select-arrow">keyboard_arrow_down</span>
              </div>

              {editForm.content_type === 'Fiction' && (
                <div className="epub-edit-field">
                  <textarea
                    className="btn-neutral epub-edit-textarea"
                    placeholder="Specify fandom / ship / dynamic (if applicable)"
                    rows={1}
                    value={editForm.fandom_details || ''}
                    ref={el => { if (el) { el.style.height = 'auto'; el.style.height = el.scrollHeight + 'px'; } }}
                    onChange={e => {
                      e.target.style.height = 'auto';
                      e.target.style.height = e.target.scrollHeight + 'px';
                      setEditForm(f => ({ ...f, fandom_details: e.target.value }));
                    }}
                  />
                </div>
              )}
            </div>
            <div className="epub-edit-footer">
              <button className="epub-edit-save" onClick={handleSaveEdit}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Progress */}
      <div className="text-center px-4 epub-progress-section">
        <p className="epub-progress-text mb-4">{progressText}</p>
        <div className="epub-progress-track">
          <div className="epub-progress-fill" style={{ width: progressWidth }}></div>
        </div>
      </div>

      {/* Info */}
        <div className="px-4 mt-4">
          {epub.summary && <>
            <div className="epub-subtitle fw-semibold">synopsis</div>
            <p className="epub-body-text epub-body-text--multiline mt-3 fw-normal">{epub.summary}</p>
          </>}
          {epub.fandom_details && <>
            <div className="epub-subtitle mt-4 fw-semibold">Notes</div>
            <p className="epub-body-text epub-body-text--multiline fw-normal mt-2 ">{epub.fandom_details}</p>
          </>}
      </div>

      {/* Buttons */}
      <div className="px-4 mt-4 pt-4 pb-4">
        <Button variant="cta" onClick={() => {
          const form = new FormData();
          form.append('epub_id', id);
          form.append('uid', currentUserId);
          fetch(`${apiUrl}/track-open.php`, {
            method: 'POST', credentials: 'include', body: form,
          }).catch(console.error);
          navigate(`/epub/${id}/read`);
        }}>
          <span className="material-symbols-rounded epub-btn-icon epub-btn-icon--cta">book_ribbon</span>
          Start Reading
        </Button>
        <div className="d-flex gap-2 mt-2 w-100">
          <Button
            variant="neutral"
            style={{ color: '#930203' }}
            onClick={handleOfflineToggle}
          >
            <span className="material-symbols-rounded epub-btn-icon">
              {isOfflineActionRunning
                ? 'progress_activity'
                : isOfflineAvailable
                  ? 'download_done'
                  : 'download'}
            </span>
            {isOfflineActionRunning
              ? 'Working...'
              : isOfflineAvailable
                ? 'Offline'
                : 'Make Offline'}
          </Button>
          <Button variant="neutral" style={{ color: '#930203' }} onClick={() => setShowAddToLists(true)}>
            <span className="material-symbols-rounded epub-btn-icon epub-btn-icon--fill">
              {isInAnyList ? 'bookmark_added' : 'bookmark'}
            </span>
            {isInAnyList ? 'Added to List' : 'Add to Lists'}
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="epub-stats d-flex justify-content-around mt-4 p-4 align-items-center">
        <div className="text-center">
          <span className="material-symbols-rounded epub-stat-icon">history_edu</span>
          <div className="epub-stat-label">{commentCount} Comments</div>
        </div>
        <div className="text-center">
          <span className="material-symbols-rounded epub-stat-icon">menu_book</span>
          <div className="epub-stat-label">
            {wordCount == null
              ? 'Word Count...'
              : `${wordCount.toLocaleString('en-US')} Words`}
          </div>
        </div>
        <div className="text-center">
          <span className="material-symbols-rounded epub-stat-icon">calendar_month</span>
          <div className="epub-stat-label">
            {new Date(epub.created_at.replace(' ', 'T') + 'Z').toLocaleDateString('en-GB')}
          </div>
        </div>
      </div>

      {epub.series_title && (
        <div className="px-4 mt-4">
          <div className="epub-subtitle fw-semibold">SERIES WORKS</div>
          <div className="d-flex flex-column gap-3 mt-3">
            {seriesWorksLoading ? (
              <p className="epub-empty-state fw-normal">Loading series works...</p>
            ) : seriesWorks.length === 0 ? (
              <p className="epub-empty-state fw-normal">No other works in this series yet.</p>
            ) : (
              seriesWorks.map((work) => (
                <SeriesWorkCard
                  key={`series-work-${work.id}`}
                  id={work.id}
                  title={work.title}
                  seriesTitle={work.series_title}
                  seriesOrder={work.series_order}
                  uploadedBy={work.uploader_name}
                  uploaderPicture={work.uploader_picture}
                  createdAt={work.created_at}
                />
              ))
            )}
          </div>
        </div>
      )}

      {/* Bookmarks */}
      <div className="bookmarks-sec px-4 mt-2 pb-5">
        <div className="epub-subtitle fw-semibold">Bookmarks</div>
        <div className="bookmark-cards mt-3">
          {bookmarksLoading ? (
            <p className="epub-empty-state fw-normal">Loading bookmarks...</p>
          ) : bookmarks.length === 0 ? (
            <p className="epub-empty-state fw-normal">No bookmarks yet.</p>
          ) : (
            bookmarks.map((bookmark) => (
              <div key={bookmark.id} className="epub-bookmark-card btn-neutral mb-3">
                <div className="d-flex align-items-start gap-3">
                  <div className="epub-bookmark-avatar">
                    {bookmark.profile_picture ? (
                      <img
                        src={
                          bookmark.profile_picture.startsWith('http')
                            ? bookmark.profile_picture
                            : `${process.env.REACT_APP_API_URL.replace('/api', '')}/${bookmark.profile_picture}`
                        }
                        alt=""
                      />
                    ) : (
                      <span>{getBookmarkInitial(bookmark.display_name)}</span>
                    )}
                  </div>
                  <div className="epub-bookmark-body">
                    <p className="epub-bookmark-date mb-2">
                      {formatBookmarkDate(bookmark.updated_at || bookmark.created_at)}
                    </p>
                    <p className="epub-bookmark-headline mb-2 fw-normal">
                      <span className="epub-bookmark-headline-user fw-medium">{bookmark.display_name}</span>
                      {' bookmarked with:'}
                    </p>
                    <p className="epub-bookmark-note mb-0 fw-normal">{bookmark.note_text}</p>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

    </div>
  );
}

export default EpubPage;
